class PerformanceMonitorService {
    constructor() {
        this.marks = new Map();
        this.observers = new Map();
        this.isInitialized = false;
        this.metrics = this.initializeMetrics();
        this.initializePerformanceObservers();
    }
    initializeMetrics() {
        return {
            pageLoadTime: 0,
            domContentLoadedTime: 0,
            firstPaintTime: 0,
            firstContentfulPaintTime: 0,
            navigationTiming: {
                dns: 0,
                tcp: 0,
                request: 0,
                response: 0,
                processing: 0,
                onLoad: 0
            },
            componentRenderTimes: {},
            apiCallTimes: {},
            userInteractionTimes: {},
            resourceTiming: []
        };
    }
    initializePerformanceObservers() {
        if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
            return;
        }
        try {
            // Observer for navigation timing
            if (PerformanceObserver.supportedEntryTypes?.includes('navigation')) {
                const navObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach((entry) => {
                        this.processNavigationTiming(entry);
                    });
                });
                navObserver.observe({ entryTypes: ['navigation'] });
                this.observers.set('navigation', navObserver);
            }
            // Observer for paint timing
            if (PerformanceObserver.supportedEntryTypes?.includes('paint')) {
                const paintObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach((entry) => {
                        if (entry.name === 'first-paint') {
                            this.metrics.firstPaintTime = entry.startTime;
                        }
                        else if (entry.name === 'first-contentful-paint') {
                            this.metrics.firstContentfulPaintTime = entry.startTime;
                        }
                    });
                });
                paintObserver.observe({ entryTypes: ['paint'] });
                this.observers.set('paint', paintObserver);
            }
            // Observer for largest contentful paint
            if (PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint')) {
                const lcpObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const lastEntry = entries[entries.length - 1];
                    if (lastEntry) {
                        this.metrics.largestContentfulPaintTime = lastEntry.startTime;
                    }
                });
                lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
                this.observers.set('lcp', lcpObserver);
            }
            // Observer for layout shift
            if (PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) {
                let clsValue = 0;
                const clsObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (!entry.hadRecentInput) {
                            clsValue += entry.value;
                            this.metrics.cumulativeLayoutShift = clsValue;
                        }
                    }
                });
                clsObserver.observe({ entryTypes: ['layout-shift'] });
                this.observers.set('cls', clsObserver);
            }
            // Observer for first input delay
            if (PerformanceObserver.supportedEntryTypes?.includes('first-input')) {
                const fidObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const firstInput = entries[0]; // Type assertion for first input entry
                    if (firstInput && 'processingStart' in firstInput) {
                        this.metrics.firstInputDelay = firstInput.processingStart - firstInput.startTime;
                    }
                });
                fidObserver.observe({ entryTypes: ['first-input'] });
                this.observers.set('fid', fidObserver);
            }
            // Observer for resource timing
            if (PerformanceObserver.supportedEntryTypes?.includes('resource')) {
                const resourceObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach((entry) => {
                        this.processResourceTiming(entry);
                    });
                });
                resourceObserver.observe({ entryTypes: ['resource'] });
                this.observers.set('resource', resourceObserver);
            }
            this.isInitialized = true;
        }
        catch (error) {
            console.warn('Failed to initialize performance observers:', error);
        }
    }
    processNavigationTiming(entry) {
        this.metrics.navigationTiming = {
            dns: entry.domainLookupEnd - entry.domainLookupStart,
            tcp: entry.connectEnd - entry.connectStart,
            request: entry.responseStart - entry.requestStart,
            response: entry.responseEnd - entry.responseStart,
            processing: entry.loadEventStart - entry.domContentLoadedEventStart,
            onLoad: entry.loadEventEnd - entry.loadEventStart
        };
        this.metrics.pageLoadTime = entry.loadEventEnd - entry.fetchStart;
        this.metrics.domContentLoadedTime = entry.domContentLoadedEventEnd - entry.fetchStart;
    }
    processResourceTiming(entry) {
        // Filter out data URIs and same-origin resources to focus on meaningful metrics
        if (entry.name.startsWith('data:') || entry.duration < 1) {
            return;
        }
        const resourceType = this.getResourceType(entry.name, entry.initiatorType);
        this.metrics.resourceTiming.push({
            name: entry.name,
            duration: entry.duration,
            size: entry.transferSize,
            type: resourceType
        });
        // Keep only the most recent 100 resource entries
        if (this.metrics.resourceTiming.length > 100) {
            this.metrics.resourceTiming = this.metrics.resourceTiming.slice(-100);
        }
    }
    getResourceType(url, initiatorType) {
        if (initiatorType) {
            return initiatorType;
        }
        const extension = url.split('.').pop()?.toLowerCase() || '';
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
        const scriptTypes = ['js', 'mjs'];
        const styleTypes = ['css'];
        if (imageTypes.includes(extension))
            return 'img';
        if (scriptTypes.includes(extension))
            return 'script';
        if (styleTypes.includes(extension))
            return 'link';
        return 'other';
    }
    // Performance marking methods
    mark(name, metadata) {
        const mark = {
            name,
            startTime: performance.now(),
            metadata
        };
        this.marks.set(name, mark);
        // Also use browser's performance.mark if available
        if ('mark' in performance) {
            performance.mark(name);
        }
    }
    measure(name, startMark) {
        const startMarkData = startMark ? this.marks.get(startMark) : null;
        const endTime = performance.now();
        let duration;
        if (startMarkData) {
            duration = endTime - startMarkData.startTime;
            startMarkData.endTime = endTime;
            startMarkData.duration = duration;
        }
        else {
            // If no start mark provided, look for existing mark with same name
            const existingMark = this.marks.get(name);
            if (existingMark && !existingMark.endTime) {
                duration = endTime - existingMark.startTime;
                existingMark.endTime = endTime;
                existingMark.duration = duration;
            }
            else {
                console.warn(`No start mark found for measure: ${name}`);
                return undefined;
            }
        }
        // Use browser's performance.measure if available
        if ('measure' in performance && startMark) {
            try {
                performance.measure(name, startMark);
            }
            catch {
                // Ignore errors if marks don't exist in browser's performance timeline
            }
        }
        return duration;
    }
    // Component performance tracking
    trackComponentRender(componentName, renderTime) {
        if (!this.metrics.componentRenderTimes[componentName]) {
            this.metrics.componentRenderTimes[componentName] = [];
        }
        this.metrics.componentRenderTimes[componentName].push(renderTime);
        // Keep only last 20 render times per component
        if (this.metrics.componentRenderTimes[componentName].length > 20) {
            this.metrics.componentRenderTimes[componentName] =
                this.metrics.componentRenderTimes[componentName].slice(-20);
        }
    }
    // API call performance tracking
    trackApiCall(endpoint, duration) {
        if (!this.metrics.apiCallTimes[endpoint]) {
            this.metrics.apiCallTimes[endpoint] = [];
        }
        this.metrics.apiCallTimes[endpoint].push(duration);
        // Keep only last 50 API call times per endpoint
        if (this.metrics.apiCallTimes[endpoint].length > 50) {
            this.metrics.apiCallTimes[endpoint] =
                this.metrics.apiCallTimes[endpoint].slice(-50);
        }
    }
    // User interaction performance tracking
    trackUserInteraction(interaction, responseTime) {
        if (!this.metrics.userInteractionTimes[interaction]) {
            this.metrics.userInteractionTimes[interaction] = [];
        }
        this.metrics.userInteractionTimes[interaction].push(responseTime);
        // Keep only last 30 interaction times per type
        if (this.metrics.userInteractionTimes[interaction].length > 30) {
            this.metrics.userInteractionTimes[interaction] =
                this.metrics.userInteractionTimes[interaction].slice(-30);
        }
    }
    // Memory monitoring
    updateMemoryUsage() {
        if ('memory' in performance) {
            const memory = performance.memory;
            this.metrics.memoryUsage = {
                used: memory.usedJSHeapSize,
                total: memory.totalJSHeapSize,
                limit: memory.jsHeapSizeLimit
            };
        }
    }
    // Get current metrics
    getMetrics() {
        this.updateMemoryUsage();
        return { ...this.metrics };
    }
    // Get performance summary
    getPerformanceSummary() {
        const metrics = this.getMetrics();
        // Calculate average component render time
        const allRenderTimes = Object.values(metrics.componentRenderTimes).flat();
        const averageComponentRender = allRenderTimes.length > 0
            ? allRenderTimes.reduce((a, b) => a + b, 0) / allRenderTimes.length
            : 0;
        // Calculate average API call time
        const allApiTimes = Object.values(metrics.apiCallTimes).flat();
        const averageApiCall = allApiTimes.length > 0
            ? allApiTimes.reduce((a, b) => a + b, 0) / allApiTimes.length
            : 0;
        // Calculate memory usage percentage
        const memoryUsage = metrics.memoryUsage
            ? (metrics.memoryUsage.used / metrics.memoryUsage.limit) * 100
            : 0;
        // Find slowest components
        const slowestComponents = Object.entries(metrics.componentRenderTimes)
            .map(([name, times]) => ({
            name,
            averageTime: times.reduce((a, b) => a + b, 0) / times.length
        }))
            .sort((a, b) => b.averageTime - a.averageTime)
            .slice(0, 5);
        // Find slowest API calls
        const slowestApiCalls = Object.entries(metrics.apiCallTimes)
            .map(([endpoint, times]) => ({
            endpoint,
            averageTime: times.reduce((a, b) => a + b, 0) / times.length
        }))
            .sort((a, b) => b.averageTime - a.averageTime)
            .slice(0, 5);
        return {
            pageLoad: metrics.pageLoadTime,
            averageComponentRender,
            averageApiCall,
            memoryUsage,
            slowestComponents,
            slowestApiCalls
        };
    }
    // Export performance data
    exportPerformanceData() {
        const metrics = this.getMetrics();
        const summary = this.getPerformanceSummary();
        return JSON.stringify({
            timestamp: new Date().toISOString(),
            summary,
            detailedMetrics: metrics,
            marks: Array.from(this.marks.values())
        }, null, 2);
    }
    // Clear performance data
    clearPerformanceData() {
        this.marks.clear();
        this.metrics = this.initializeMetrics();
    }
    // Cleanup observers
    cleanup() {
        this.observers.forEach((observer) => {
            observer.disconnect();
        });
        this.observers.clear();
    }
}
// Global instance
export const performanceMonitor = new PerformanceMonitorService();
// React hook for performance monitoring
export function usePerformanceMonitor() {
    const mark = (name, metadata) => {
        performanceMonitor.mark(name, metadata);
    };
    const measure = (name, startMark) => {
        return performanceMonitor.measure(name, startMark);
    };
    const trackComponent = (componentName, renderTime) => {
        performanceMonitor.trackComponentRender(componentName, renderTime);
    };
    const trackApi = (endpoint, duration) => {
        performanceMonitor.trackApiCall(endpoint, duration);
    };
    const getMetrics = () => performanceMonitor.getMetrics();
    const getSummary = () => performanceMonitor.getPerformanceSummary();
    return {
        mark,
        measure,
        trackComponent,
        trackApi,
        getMetrics,
        getSummary
    };
}
//# sourceMappingURL=performance-monitor.js.map